/**
 * scopingRailModel (017, US2) — pure mapping from the scoping snapshot to the
 * read-only rail rows (data-model §3). The `briefing_draft` item shapes are
 * loose (`unknown[]`) in the shared schema and there is no live client read
 * path yet, so this coerces defensively (string | { text }) and degrades to a
 * safe "nothing captured" rail. When the gateway emits the live scoping
 * snapshot, feed it here unchanged.
 *
 * Status rule: a section is `done` when its field(s) are populated; the first
 * not-yet-done section (in order) is `active`; the rest are `todo`.
 */

import type { ScopingArtifact } from './ScopingArtifactCard';

// ---------------------------------------------------------------------------
// Rail row types — relocated here from ScopingGuideRail.tsx (T002/spec-048) so
// the rail component can be deleted without breaking its type importers.
// ---------------------------------------------------------------------------

export type ScopingSectionStatus = 'done' | 'active' | 'todo';

export interface ScopingRailRow {
  id: string;
  name: string;
  sub: string;
  status: ScopingSectionStatus;
  /** `artifactStatus` is the captured artifact's lifecycle state (039 vocabulary:
   *  `draft` while scoping is open, `confirmed` once the scope is locked). */
  captured?: { payload: ScopingArtifact; artifactStatus: 'draft' | 'confirmed' };
}

export const SCOPING_SECTIONS = [
  { id: 'orient', name: 'Setting the stage', sub: 'How this call works & what comes next' },
  { id: 'frame', name: 'Framing the decision', sub: 'The call this project has to land' },
  { id: 'bets', name: "The bets you're placing", sub: 'Your hypotheses for a win' },
  { id: 'assume', name: 'Assumptions to test', sub: 'What quietly has to be true' },
  { id: 'learn', name: "What we'll learn", sub: 'The questions research will answer' },
  { id: 'lock', name: 'Locking the scope', sub: 'Agreement & what happens next' },
] as const;

export type ScopingSectionId = (typeof SCOPING_SECTIONS)[number]['id'];

/** Loose view of the scoping snapshot — every field optional/unknown by design. */
export interface ScopingSnapshotInput {
  briefing_draft?: {
    decision_intent?: unknown;
    project_type?: unknown;
    rationale?: unknown;
    key_bets?: unknown;
    key_assumptions?: unknown;
    learning_goals?: unknown;
    scope?: unknown;
    purpose?: unknown;
    project_status?: unknown;
  } | null;
  /**
   * Cloud-derived scoreboard — `current_objective.section_id` drives the active
   * stage; `checklist` (per completion-gate-field status) drives progress
   * (data-model §3). Loose/optional by design; the progress adapter coerces.
   */
  scoreboard?: {
    checklist?: Array<{ key?: unknown; status?: unknown; count?: unknown }> | null;
    current_objective?: { id?: unknown; key?: unknown; section_id?: unknown } | null;
  } | null;
}

function asText(x: unknown): string {
  if (typeof x === 'string') return x.trim();
  if (x && typeof x === 'object' && typeof (x as { text?: unknown }).text === 'string') {
    return ((x as { text: string }).text).trim();
  }
  return '';
}

function asList(x: unknown): unknown[] {
  return Array.isArray(x) ? x : [];
}

function asProjectType(x: unknown): 'Validation' | 'Exploratory' | undefined {
  return x === 'Validation' || x === 'Exploratory' ? x : undefined;
}

/**
 * Build the 6 rail rows from a (possibly absent / partial) scoping snapshot.
 *
 * Captured-artifact status (039 draft/confirmed) is derived from the SCOPE lock
 * state, not a hardcoded value: while scoping is open every captured artifact is
 * a `draft` (provisional, still being refined); once the scope is locked
 * (`project_status` past the open statuses — see {@link projectToBriefingDraft},
 * which normalises any locked status to `'ready'`) they are all `confirmed`. The
 * old code hardcoded `'final'`, so the rail always read "added to scope" even
 * mid-scoping — the badge could never show draft.
 */
export function buildScopingRailRows(
  snapshot: ScopingSnapshotInput | null | undefined,
  activeSectionId?: ScopingSectionId,
): ScopingRailRow[] {
  const d = snapshot?.briefing_draft ?? null;

  const decisionIntent = asText(d?.decision_intent);

  // 037 — a captured bet is a string, the pre-037 `{ text }` shape, OR the typed
  // `{ statement, bet_type, assumptions[] }` shape (scoping-seed-graph capture
  // contract). Read `statement` → text, and surface `bet_type` as the TYPE pill
  // (parallel to assumption_type / goal_type); rationale/provenance is separate.
  const obj = (x: unknown): Record<string, unknown> =>
    x && typeof x === 'object' ? (x as Record<string, unknown>) : {};
  const betText = (it: unknown): string => asText(it) || asText(obj(it).statement);
  const asRisk = (v: unknown): 'low' | 'medium' | 'high' | 'critical' | undefined =>
    v === 'low' || v === 'medium' || v === 'high' || v === 'critical' ? v : undefined;
  // Surface a captured assumption's type (`assumption_type`) and risk (`risk_level`)
  // — the evidence axis (validated/partly/net-new) is retired.
  const assumptionExtras = (a: unknown): { type?: string; risk?: 'low' | 'medium' | 'high' | 'critical' } => {
    const o = obj(a);
    const type = typeof o.type === 'string' ? o.type : typeof o.assumption_type === 'string' ? o.assumption_type : undefined;
    const risk = asRisk(o.risk) ?? asRisk(o.risk_level);
    return { ...(type ? { type } : {}), ...(risk ? { risk } : {}) };
  };

  const betList = asList(d?.key_bets);
  const bets = betList
    .map((it) => {
      const o = obj(it);
      // bet_type is the TYPE (mirrors assumption_type) — surfaced in the type pill.
      const type = typeof o.type === 'string' ? o.type : typeof o.bet_type === 'string' ? o.bet_type : undefined;
      // provenance/rationale is the ORIGIN — kept distinct from the type pill.
      const provenance =
        typeof o.provenance === 'string'
          ? o.provenance
          : typeof o.rationale === 'string'
            ? o.rationale
            : typeof o.tag === 'string'
              ? o.tag
              : undefined;
      return { text: betText(it), ...(type ? { type } : {}), ...(provenance ? { provenance } : {}) };
    })
    .filter((b) => b.text.length > 0);

  // Assumptions: pre-037 lived top-level (`key_assumptions`, flat); 037 embeds
  // them per bet (`key_bets[].assumptions[]`). Prefer the embedded shape (grouped
  // by parent bet statement); fall back to the flat top-level shape for back-compat.
  const embeddedGroups = betList
    .map((it) => {
      const items = asList(obj(it).assumptions)
        .map((a) => ({ text: asText(a) || asText(obj(a).statement), ...assumptionExtras(a) }))
        .filter((x) => x.text.length > 0);
      return { bet: betText(it), items };
    })
    .filter((g) => g.items.length > 0);
  const topLevelAssumptions = asList(d?.key_assumptions)
    .map((it) => ({ text: asText(it) || asText(obj(it).statement), ...assumptionExtras(it) }))
    .filter((a) => a.text.length > 0);
  const assumptionGroups =
    embeddedGroups.length > 0
      ? embeddedGroups
      : topLevelAssumptions.length > 0
        ? [{ bet: '', items: topLevelAssumptions }]
        : [];
  const assumptionCount = assumptionGroups.reduce((n, g) => n + g.items.length, 0);

  // Learning goals: string | `{ text }` (pre-037) | `{ name, goal_type }` (037).
  // Surface `goal_type` as a type pill alongside the goal text.
  const goals = asList(d?.learning_goals)
    .map((g) => {
      const o = obj(g);
      const text = asText(g) || asText(o.name);
      const type = typeof o.type === 'string' ? o.type : typeof o.goal_type === 'string' ? o.goal_type : undefined;
      return { text, ...(type ? { type } : {}) };
    })
    .filter((g) => g.text.length > 0);
  const lockReady = d?.project_status === 'ready';
  // Captured artifacts are `draft` until the scope is locked, then `confirmed`.
  const capturedStatus: 'draft' | 'confirmed' = lockReady ? 'confirmed' : 'draft';

  const captured: Partial<Record<(typeof SCOPING_SECTIONS)[number]['id'], ScopingArtifact>> = {};
  if (decisionIntent) {
    captured.frame = {
      kind: 'decision',
      intent: decisionIntent,
      projectType: mapProjectTypeLoose(d?.project_type),
      rationale: asText(d?.rationale) || undefined,
    };
  }
  if (bets.length > 0) captured.bets = { kind: 'bets', items: bets };
  if (assumptionGroups.length > 0) {
    // Embedded (037): one group per bet, headed by the bet statement. Pre-037
    // back-compat: a single empty-label group ⇒ the card renders just the
    // bulleted assumptions (no redundant heading under the card title).
    captured.assume = { kind: 'assumptions', groups: assumptionGroups };
  }
  if (goals.length > 0) captured.learn = { kind: 'goals', items: goals };
  if (lockReady) {
    captured.lock = {
      kind: 'scope',
      summary: asText(d?.scope) || asText(d?.purpose) || undefined,
      items: [
        decisionIntent ? `Decision: ${decisionIntent}` : null,
        bets.length ? `${bets.length} bet${bets.length === 1 ? '' : 's'}` : null,
        assumptionCount ? `${assumptionCount} assumption${assumptionCount === 1 ? '' : 's'}` : null,
        goals.length ? `${goals.length} learning goal${goals.length === 1 ? '' : 's'}` : null,
      ].filter((x): x is string => x !== null),
    };
  }

  const anyData = Boolean(decisionIntent || bets.length || assumptionCount || goals.length || lockReady);

  const hasData: Record<(typeof SCOPING_SECTIONS)[number]['id'], boolean> = {
    orient: anyData,
    frame: Boolean(decisionIntent),
    bets: bets.length > 0,
    assume: assumptionCount > 0,
    learn: goals.length > 0,
    lock: lockReady,
  };

  // Active stage: the scoreboard's current_objective when it maps to a not-yet-
  // done section (data-model §3), else the first not-done section in order.
  const firstNotDone = SCOPING_SECTIONS.find((s) => !hasData[s.id])?.id;
  const activeId =
    activeSectionId && !hasData[activeSectionId] ? activeSectionId : firstNotDone;

  return SCOPING_SECTIONS.map((section) => {
    const done = hasData[section.id];
    const status: ScopingRailRow['status'] = done
      ? 'done'
      : section.id === activeId
        ? 'active'
        : 'todo';
    const payload =
      section.id === 'orient'
        ? anyData
          ? ({ kind: 'note', text: 'Oriented on the six steps — we work back from the decision.' } as ScopingArtifact)
          : undefined
        : captured[section.id];
    // The orient note is informational (not a captured draft), so it never reads
    // "draft"; every other section follows the scope-lock-derived status.
    const artifactStatus = section.id === 'orient' ? 'confirmed' : capturedStatus;
    return {
      id: section.id,
      name: section.name,
      sub: section.sub,
      status,
      captured: payload ? { payload, artifactStatus } : undefined,
    };
  });
}

// ---------------------------------------------------------------------------
// Resume recap (ScOrientCard welcome-back)
// ---------------------------------------------------------------------------

/**
 * What each section contributes to "we've got ___ down". `frame` is named
 * without a count (there is one decision); the rest are counted. Sections absent
 * here are never named: `orient` is informational (it flips `done` on any data at
 * all) and `lock` is the finish line, not a capture.
 */
const RESUME_CAPTURED: Partial<Record<ScopingSectionId, (n: number) => string>> = {
  frame: () => 'your decision',
  bets: (n) => `${n} ${n === 1 ? 'bet' : 'bets'}`,
  assume: (n) => `${n} ${n === 1 ? 'assumption' : 'assumptions'}`,
  learn: (n) => `${n} ${n === 1 ? 'learning goal' : 'learning goals'}`,
};

/** What each section reads as when it's the next thing to do. */
const RESUME_NEXT: Record<ScopingSectionId, string> = {
  orient: 'getting oriented',
  frame: 'framing the decision',
  bets: "the bets you're placing",
  assume: 'the assumptions underneath them',
  learn: "what we'll learn",
  lock: 'locking the scope',
};

/** How many artifacts a captured payload holds (0 when it isn't a counted kind). */
function capturedCount(payload: ScopingArtifact): number {
  if (payload.kind === 'bets') return payload.items.length;
  if (payload.kind === 'goals') return payload.items.length;
  if (payload.kind === 'assumptions') {
    return payload.groups.reduce((n, g) => n + g.items.length, 0);
  }
  return 0;
}

/** "a" · "a and b" · "a, b and c" */
function serialise(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * A one-line "where we got to" for the welcome-back card — e.g.
 * "We've got your decision and 3 bets down. Next up: the assumptions underneath them."
 *
 * Returns `null` when there is nothing worth saying, so the caller renders
 * nothing rather than an empty or dishonest recap. Deliberately a sentence, not a
 * list: it is the only progress signal on the orient screen once the rail is
 * retired (ANA-639), and a list there would just be the rail again.
 */
export function buildScopingResumeLine(rows: ScopingRailRow[]): string | null {
  const captured: string[] = [];
  for (const r of rows) {
    if (r.status !== 'done') continue;
    const fragment = RESUME_CAPTURED[r.id as ScopingSectionId];
    if (!fragment) continue;
    if (r.id === 'frame') {
      captured.push(fragment(0));
      continue;
    }
    // A counted section only earns a mention when it actually holds something —
    // a `done` row with an empty (or absent) payload must never read "0 bets".
    const n = r.captured ? capturedCount(r.captured.payload) : 0;
    if (n > 0) captured.push(fragment(n));
  }
  if (captured.length === 0) return null;

  const list = serialise(captured);
  const next = rows.find((r) => r.status === 'active');
  return next
    ? `We've got ${list} down. Next up: ${RESUME_NEXT[next.id as ScopingSectionId]}.`
    : `We've got ${list} down — that's the full scope.`;
}

/**
 * Loose view of the project entity (briefing-context `data.project`) — the
 * HYBRID base for the rail (017). The live scoping conversation never writes
 * `briefing_draft` today (n8n emits `scoreboard`/`agent_notes` only), so the
 * captured scope is read from the project: `decision_intent` (+ `project_type`)
 * and `snapshot.bets` / `snapshot.learning_goals`. The live `briefing_draft` is
 * overlaid per-field when/if the pipeline starts writing it.
 */
export interface ScopingProjectInput {
  decision_intent?: unknown;
  project_type?: unknown;
  status?: unknown;
  snapshot?: { bets?: unknown; learning_goals?: unknown } | null;
}

type BriefingDraft = NonNullable<ScopingSnapshotInput['briefing_draft']>;

function mapProjectTypeLoose(x: unknown): 'Validation' | 'Exploratory' | undefined {
  const s = typeof x === 'string' ? x.toLowerCase() : '';
  if (s === 'validation') return 'Validation';
  if (s === 'exploratory') return 'Exploratory';
  return asProjectType(x);
}

/** Adapt the project entity into a `briefing_draft`-shaped base for the rail. */
export function projectToBriefingDraft(
  project: ScopingProjectInput | null | undefined,
): BriefingDraft {
  const snap = (project?.snapshot ?? {}) as { bets?: unknown; learning_goals?: unknown };
  const bets = asList(snap.bets)
    .map((b) => {
      const o = b && typeof b === 'object' ? (b as Record<string, unknown>) : {};
      return {
        text: asText(o.statement ?? o.text ?? b),
        // The project snapshot's bet `type` is the bet_type → downstream surfaces it
        // as the TYPE pill (buildScopingRailRows maps bet_type → type).
        ...(typeof o.type === 'string' ? { bet_type: o.type } : {}),
      };
    })
    .filter((x) => x.text.length > 0);
  // Assumptions are nested under each bet's `top_assumption` on the project snapshot.
  const assumptions = asList(snap.bets).flatMap((b) => {
    const ta = b && typeof b === 'object' ? (b as Record<string, unknown>).top_assumption : null;
    if (!ta || typeof ta !== 'object') return [];
    const text = asText((ta as Record<string, unknown>).statement);
    return text ? [{ text }] : [];
  });
  const goals = asList(snap.learning_goals)
    .map((g) =>
      asText(
        g && typeof g === 'object'
          ? ((g as Record<string, unknown>).name ?? (g as Record<string, unknown>).text)
          : g,
      ),
    )
    .filter((t) => t.length > 0);
  const decision = asText(project?.decision_intent);
  const status = typeof project?.status === 'string' ? project.status : '';
  return {
    decision_intent: decision || undefined,
    project_type: mapProjectTypeLoose(project?.project_type),
    key_bets: bets,
    key_assumptions: assumptions,
    learning_goals: goals,
    scope: decision || undefined,
    // The rail's lock section is "done" once scope is locked. Any status past the
    // open scoping statuses counts as locked (037: `seeding` is the post-lock state
    // the promotion writes; also `planning`/`ready`/`active`/…), so normalise them
    // all to 'ready' — otherwise a freshly-seeded project shows lock as not-done.
    project_status: isScopeLocked(status) ? 'ready' : status || undefined,
  } as BriefingDraft;
}

function isNonEmpty(v: unknown): boolean {
  if (typeof v === 'string') return v.trim().length > 0;
  if (Array.isArray(v)) return v.length > 0;
  return v != null;
}

/** Field-level merge: the live scoping `briefing_draft` wins per-field when it
 *  carries a non-empty value, otherwise the project-derived base shows. */
export function mergeBriefingDrafts(
  base: BriefingDraft,
  live: BriefingDraft | null | undefined,
): BriefingDraft {
  if (!live) return base;
  const out: Record<string, unknown> = { ...base };
  for (const k of Object.keys(live)) {
    // The project entity owns the lock state — never let stale live session
    // memory (e.g. project_status:'scoping') override a present ready/active
    // base. (When the base has no status, live may still fill it.)
    if (k === 'project_status' && isNonEmpty(out.project_status)) continue;
    const v = (live as Record<string, unknown>)[k];
    if (isNonEmpty(v)) out[k] = v;
  }
  return out as BriefingDraft;
}

/**
 * Build rail rows from the briefing-context payload — the project entity as the
 * base, the live scoping snapshot's `briefing_draft` overlaid per-field (017
 * hybrid). Reuses {@link buildScopingRailRows} for the section/status mapping.
 */
/** Defensive scoreboard → active-section mapping: returns a section id only when
 *  the scoreboard's `current_objective` matches a known section. Prefers
 *  `section_id` (the gateway's canonical slug — spec 023 R-rail-wiring), then
 *  falls back to `id`/`key` for older snapshot shapes. */
function scoreboardActiveSection(
  scoreboard: ScopingSnapshotInput['scoreboard'],
): ScopingSectionId | undefined {
  const co = scoreboard?.current_objective;
  if (!co || typeof co !== 'object') return undefined;
  const candidate = [
    (co as Record<string, unknown>).section_id,
    (co as Record<string, unknown>).id,
    (co as Record<string, unknown>).key,
  ].find((v): v is string => typeof v === 'string');
  return SCOPING_SECTIONS.find((s) => s.id === candidate)?.id;
}

export function buildScopingRailRowsFromContext(args: {
  project?: ScopingProjectInput | null;
  snapshot?: ScopingSnapshotInput | null;
}): ScopingRailRow[] {
  const base = projectToBriefingDraft(args.project);
  const merged = mergeBriefingDrafts(base, args.snapshot?.briefing_draft ?? null);
  return buildScopingRailRows(
    { briefing_draft: merged },
    scoreboardActiveSection(args.snapshot?.scoreboard),
  );
}

/** Project statuses where scoping is still open (not yet locked). */
const SCOPING_OPEN_STATUSES = new Set(['draft', 'created', 'scoping']);

/**
 * True when the project's scope is LOCKED — i.e. scoping has completed and the
 * project has moved past the open statuses (`ready`/`active`/…). The scoping
 * page uses this to avoid auto-starting a fresh call on a finished project and
 * to show the locked state instead (FR-005). Unknown/absent status ⇒ not locked
 * (treat as still-scoping so the normal flow runs).
 */
export function isScopeLocked(status: unknown): boolean {
  return typeof status === 'string' && status.length > 0 && !SCOPING_OPEN_STATUSES.has(status);
}
